<template>
  <div class="control is-horizontal eigenmarken-kategorien-control">
    <div class="control-label">
      <label-name labelKey="SITE-PWA-EIGENMARKEN_KATEGORIEN"></label-name>
    </div>

    <div class="control eigenmarken-container">
      <!-- CATEGORY ROWS LIST -->
      <div v-if="items.length > 0" class="category-list">
        <div
          v-for="(item, index) in items"
          :key="'cat-' + index"
          class="category-card"
        >
          <!-- 1. INDEX & REORDER HANDLE -->
          <div class="category-handle">
            <span class="tag is-rounded is-light category-index">#{{ index + 1 }}</span>
            <div class="reorder-buttons">
              <button
                type="button"
                class="button is-small is-light btn-reorder"
                :disabled="index === 0"
                @click="moveUp(index)"
                title="Nach oben verschieben"
              >
                <span class="material-symbols-outlined">keyboard_arrow_up</span>
              </button>
              <button
                type="button"
                class="button is-small is-light btn-reorder"
                :disabled="index === items.length - 1"
                @click="moveDown(index)"
                title="Nach unten verschieben"
              >
                <span class="material-symbols-outlined">keyboard_arrow_down</span>
              </button>
            </div>
          </div>

          <!-- 2. INPUT FIELD -->
          <div class="category-input-wrap">
            <input
              :ref="'catInput_' + index"
              type="text"
              class="input is-small category-input"
              v-model="items[index]"
              placeholder="z.B. Sonnentor, Eigenmarke, ..."
              @input="emitUpdate"
              @keydown.enter.prevent="onEnterPressed(index)"
            />
          </div>

          <!-- 3. ACTION BUTTONS (DELETE) -->
          <div class="category-actions">
            <button
              type="button"
              class="button is-small is-danger is-outline is-icon-only btn-card-action btn-delete"
              title="Kategorie entfernen"
              @click="removeCategory(index)"
            >
              <span class="material-symbols-outlined">delete</span>
            </button>
          </div>
        </div>
      </div>

      <!-- EMPTY STATE -->
      <div v-else class="empty-category-state">
        <span class="material-symbols-outlined empty-state-icon">category</span>
        <p class="mb-2">Noch keine Eigenmarken-Kategorien hinterlegt.</p>
        <button
          type="button"
          class="button is-blue is-outline is-small"
          @click="addCategory"
        >
          <span class="material-symbols-outlined">add</span>
          Erste Kategorie hinzufügen
        </button>
      </div>

      <!-- TOOLBAR -->
      <div v-if="items.length > 0" class="category-toolbar">
        <button
          type="button"
          class="button is-blue is-outline is-small btn-add-cat"
          @click="addCategory"
        >
          <span class="material-symbols-outlined">add</span>
          Kategorie hinzufügen
        </button>

        <span class="category-counter tag is-light is-small ml-2">
          {{ filledItemsCount }} Kategorie(n) hinterlegt
        </span>
      </div>

      <!-- INFO HELP TEXT -->
      <span class="help is-info-text">
        Kategorienamen der Eigenmarken für Filterung und Gruppierung im Shop.
      </span>

      <!-- FOOTER: RAW JSON PANEL -->
      <pharmacy-raw-json-panel
        :json-data="serializeData()"
        title="JSON-Rohdaten (Eigenmarken-Kategorien)"
        :rows="6"
        placeholder='["Sonnentor2", "Noch etwas", "etc."]'
        @apply="onApplyRawJson"
      />
    </div>
  </div>
</template>

<script>
import PharmacyRawJsonPanel from './incl-pharmacy-raw-json-panel.vue';

export default {
  name: 'PharmacyEigenmarkenKategorien',

  components: {
    PharmacyRawJsonPanel
  },

  props: {
    // Array of strings ["Sonnentor2", "Noch etwas", ...] or JSON string
    value: {
      type: [Array, String],
      default: () => []
    }
  },

  data() {
    return {
      items: this.parseValue(this.value),
      isInternalUpdate: false
    };
  },

  computed: {
    filledItemsCount() {
      return this.items.filter(c => typeof c === 'string' && c.trim() !== '').length;
    }
  },

  watch: {
    value: {
      deep: true,
      handler(newVal) {
        if (this.isInternalUpdate) {
          this.isInternalUpdate = false;
          return;
        }
        this.items = this.parseValue(newVal);
      }
    }
  },

  methods: {
    parseValue(val) {
      let data = val;
      if (typeof val === 'string' && val.trim() !== '') {
        try {
          data = JSON.parse(val);
        } catch (e) {
          data = [];
        }
      }
      if (!Array.isArray(data)) {
        data = [];
      }

      return data.map(item => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && item.name) return String(item.name);
        return String(item || '');
      });
    },

    serializeData() {
      return this.items
        .map(c => (typeof c === 'string' ? c : '').trim())
        .filter(c => c !== '');
    },

    emitUpdate() {
      const payload = this.serializeData();
      this.isInternalUpdate = true;
      this.$emit('input', payload);
      this.$emit('change', payload);

      if (this.showRawJson) {
        this.rawJsonInput = JSON.stringify(payload, null, 2);
      }
    },

    addCategory() {
      this.items.push('');
      const newIndex = this.items.length - 1;
      this.$nextTick(() => {
        const refName = 'catInput_' + newIndex;
        if (this.$refs[refName] && this.$refs[refName][0]) {
          this.$refs[refName][0].focus();
        }
      });
      this.emitUpdate();
    },

    onEnterPressed(index) {
      this.items.splice(index + 1, 0, '');
      this.$nextTick(() => {
        const refName = 'catInput_' + (index + 1);
        if (this.$refs[refName] && this.$refs[refName][0]) {
          this.$refs[refName][0].focus();
        }
      });
      this.emitUpdate();
    },

    removeCategory(index) {
      this.items.splice(index, 1);
      this.emitUpdate();
    },

    moveUp(index) {
      if (index <= 0) return;
      const item = this.items.splice(index, 1)[0];
      this.items.splice(index - 1, 0, item);
      this.emitUpdate();
    },

    moveDown(index) {
      if (index >= this.items.length - 1) return;
      const item = this.items.splice(index, 1)[0];
      this.items.splice(index + 1, 0, item);
      this.emitUpdate();
    },

    onApplyRawJson(parsed, done) {
      if (!Array.isArray(parsed)) {
        return done('Das JSON muss ein Array von Strings sein (z.B. ["Sonnentor2", "Noch etwas"]).');
      }
      this.items = this.parseValue(parsed);
      this.emitUpdate();
      done();
    }
  }
};
</script>

<style lang="scss" scoped>
.eigenmarken-kategorien-control {
  margin-top: 10px;
  margin-bottom: 20px;
  align-items: flex-start;

  > .control-label {
    padding-top: 6px;
  }
}

.eigenmarken-container {
  flex: 1;
  max-width: 880px;
  width: 100%;
}

/* LIST OF CATEGORY CARDS */
.category-list,
.category-toolbar,
.empty-category-state {
  max-width: 450px;
}

.category-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.category-card {
  display: flex;
  align-items: center;
  background-color: #fff;
  border: 1px solid #e1e6eb;
  border-radius: 6px;
  padding: 8px 12px;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  }
}

/* HANDLE & NUMBERING */
.category-handle {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 60px;

  .category-index {
    font-size: 11px;
    font-weight: 700;
    color: #555;
    background-color: #f0f3f6;
    padding: 0 6px;
    height: 20px;
    line-height: 20px;
  }

  .reorder-buttons {
    display: flex;
    flex-direction: column;
    gap: 1px;

    .btn-reorder {
      width: 20px !important;
      height: 16px !important;
      min-width: 20px !important;
      padding: 0 !important;
      border: none !important;
      background: transparent !important;
      color: #7a7a7a !important;
      cursor: pointer;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      line-height: 1 !important;

      .material-symbols-outlined {
        margin: 0 !important;
        padding: 0 !important;
        font-size: 15px !important;
        line-height: 1 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
      }

      &:hover:not(:disabled) {
        color: #2780e3 !important;
        background-color: #f0f4f8 !important;
        border-radius: 2px !important;
      }

      &:disabled {
        opacity: 0.3 !important;
        cursor: not-allowed;
      }
    }
  }
}

/* INPUT WRAP */
.category-input-wrap {
  flex: 1;
  min-width: 0;

  .category-input {
    height: 32px;
    font-size: 13px;
    border-radius: 3px;
    border-color: #dbdbdb;
    padding-left: 10px;
    padding-right: 10px;
    width: 100%;

    &:focus {
      border-color: #45a3d2;
    }
  }
}

/* ACTION BUTTONS */
.category-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;

  .button.is-icon-only,
  .btn-card-action {
    height: 28px !important;
    width: 28px !important;
    min-width: 28px !important;
    border-radius: 4px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 !important;
    line-height: 1 !important;

    .material-symbols-outlined,
    .icon,
    span {
      margin: 0 !important;
      margin-right: 0 !important;
      margin-left: 0 !important;
      padding: 0 !important;
      font-size: 15px !important;
      line-height: 1 !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      text-align: center !important;
    }

    &.btn-delete {
      color: #fff !important;
      background-color: #e74c3c !important;
      border-color: #e74c3c !important;

      &:hover {
        background-color: #c0392b !important;
        border-color: #c0392b !important;
      }
    }
  }
}

/* EMPTY STATE */
.empty-category-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: #fafbfc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  color: #64748b;
  text-align: center;
  font-size: 13px;

  .empty-state-icon {
    font-size: 32px;
    color: #94a3b8;
    margin-bottom: 6px;
  }
}

/* TOOLBAR */
.category-toolbar {
  display: flex;
  align-items: center;
  margin-top: 10px;

  .btn-add-cat {
    height: 28px;
    font-size: 12px;
    border-radius: 4px;

    .material-symbols-outlined {
      font-size: 16px;
      margin-right: 4px;
    }
  }

  .category-counter {
    height: 24px;
    font-size: 11px;
    color: #64748b;
  }
}

.help.is-info-text {
  display: block;
  font-size: 11px;
  color: #7a7a7a;
  margin-top: 8px;
  margin-bottom: 0;
  line-height: 1.4;
}

/* FOOTER JSON ACTION */
</style>

